import * as React from "react";
import { Link, Redirect, RouteComponentProps } from "react-router-dom";
import * as clientViews from "@app/admin/client-administration/views";
import { AccountContext, AccountProvider } from "@app/client-account/AccountContext";
import { toSSOConfig } from "@app/client-account/models";
import { SSOConfig, SSOSchemesService } from "@app/sso-schemes";
import { Unauthorized } from "@app/shared/ErrorPages";
import { DashboardsProvider } from "@app/admin/contexts/DashboardsContext";
import { toast } from "react-toastify";
import { ClientContext, ClientProvider } from "@app/admin/contexts/ClientContext";
import { useSSO } from "@app/client-account/hooks";
import { InsurancesProvider } from "@app/admin/contexts/InsurancesContext";
import moment from "moment";
import { ModalLink } from "@app/shared/Modals";
import { JSX } from "react";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { Breadcrumb } from "@app/shared/breadcrumbs/breadcrumb";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import ProjectService from "@app/projects/projectService";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { WizardNavigationBar } from "@app/shared/qes-standards/WizardNavigationBar";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
interface ClientAdminRouteProps {
  clientId: string;
}

type PropsType = RouteComponentProps<ClientAdminRouteProps>;

type ClientViews = "Profile" | "SSO" | "Roles" | "Default User Roles" | "Domains and Dashboards" | "Snowflake Warehouse Settings" | "Snowflake Data Sharing";

const ssoSchemesService = new SSOSchemesService();
const projectService = new ProjectService();
const ClientDetailWithContexts: React.FC<React.PropsWithChildren<PropsType>> = (props: PropsType) => {
  // meta state
  const [error, setError] = React.useState("");
  const [activeView, setActiveView] = React.useState<ClientViews>("Profile");
  const [loading, setLoading] = React.useState(true);
  const [redirect, setRedirect] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const user = React.useContext(UserContext);
  const userIsQuestAdmin = user.roles.includes(Roles.QUEST_ADMIN);
  const userIsSnowflakeWarehouseAdmin = user.roles.includes(Roles.SNOWFLAKE_CLIENT_WAREHOUSE_SETTINGS);
  const userIsDataShareAdmin = user.roles.includes(Roles.SNOWFLAKE_DATA_SHARING_ADMIN);
  const userIsDataShareViewer = user.roles.includes(Roles.SNOWFLAKE_DATA_SHARING_VIEWER);
  const [exportingProjectConfigs, setExportingProjectConfigs] = React.useState(false);

  const clientContext = React.useContext(ClientContext);

  const accountContext = React.useContext(AccountContext);
  const { hasSSO, loadSSO, sso } = useSSO(); // see comment at loadSSO below

  React.useEffect(() => {
    setLoading(true);
    // this sets the id for the context used by SSO
    accountContext.setClientId(clientContext.clientId);
    if (clientContext.clientId !== undefined) {
      setLoading(false);
    }
  }, [clientContext.clientId, clientContext.clientName, clientContext.isActive]);

  React.useEffect(() => {
    // Importing and calling this here is necessary
    // If we don't, the clientcontext doesn't consider any pre-existing SSO data for validation until
    // after the SSO tab is clicked, which could lead to overwriting/resetting important fields
    if (accountContext.clientId && userIsQuestAdmin) {
      loadSSO();
    }
  }, [accountContext.clientId]);

  React.useEffect(() => {
    if (!userIsQuestAdmin) {
      clientContext.setValidateSSO(false);
    }
  }, []);

  React.useEffect(() => {
    if (hasSSO) {
      clientContext.initializeSSO(sso);
    }
  }, [hasSSO]);

  const disablePanels = React.useMemo(() => {
    return loading || clientContext.clientId === undefined || !clientContext.isActive;
  }, [loading, clientContext.clientId, clientContext.isActive]);

  if (clientContext.clientId !== undefined && clientContext.clientId !== user.clientId &&
    !(userIsQuestAdmin)) {
    return <Unauthorized />;
  }

  const breadcrumbs: (string | Breadcrumb)[] = React.useMemo(() => ([
    clientContext?.clientName || "Loading..."
  ]), [clientContext?.clientName, loading]);

  const deactivateClient = () => {
    setLoading(true);
    try {
      clientContext.deactivateClient().then(() => {
        // refreshing after updating the active status
        clientContext.getClient(clientContext.clientId);
        setLoading(false);
      });
    } catch {
      setLoading(false);
      setError("Please try again later.");
    }
  }

  const activateClient = () => {
    setLoading(true);
    try {
      clientContext.activateClient().then(() => {
        // refreshing after updating the active status
        clientContext.getClient(clientContext.clientId);
        setLoading(false);
      });
    } catch {
      setLoading(false);
      setError("Please try again later.");
    }
  }

  const createGuestLogins = () => {
    setLoading(true);
    try {
      clientContext.createGuestLogins().then(() => {
        clientContext.getClient(clientContext.clientId);
        toast(<span>Guest logins for {clientContext.clientName} have been created.</span>,
          {
            containerId: "z-index-9999",
            type: "success",
          },
        );
        setLoading(false);
      });
    } catch {
      setLoading(false);
      setError("Please try again later.");
    }
  }


  /* oxlint-disable sort-keys */
  // Unfortunately can't use ClientViews as the key type here
  const clientDetailViews: { [key: string]: JSX.Element } = React.useMemo(() => {
    const views = {
      "Profile": (
        <div className="col-8">
          <PageSection>
            {!!error && <QESAlert type="danger" persist={true}>{error}</QESAlert>}
            <clientViews.ClientInfo disabled={disablePanels || !(userIsQuestAdmin)} />
          </PageSection>
        </div>
      ),
      "SSO": (
        <div className="col-8">
          <PageSection>
            <clientViews.ClientSSO clientId={clientContext.clientId} disabled={disablePanels || !(userIsQuestAdmin)} />
          </PageSection>
        </div>
      ),
      "Roles": (
        <div className="col-12">
          <PageSection>
            <clientViews.ClientRoles disabled={disablePanels} />
          </PageSection>
        </div>
      ),
      "Default User Roles": (
        <div className="col-12">
          <PageSection>
            <clientViews.ClientDefaultUserRoles disabled={disablePanels} />
          </PageSection>
        </div>
      ),
      "Domains and Dashboards": (
        <div className="col-8">
          <clientViews.ClientDomainsAndDashboards disabled={disablePanels} />
        </div>
      ),
      "Software Releases": (
        <div className="col-8">
          <clientViews.ClientSoftwareReleases disabled={disablePanels} />
        </div>
      ),
      "Authorized Insurances": (
        <div className="col-8">
          <InsurancesProvider availableInsurances={clientContext.availableInsurances}>
            <clientViews.ClientAuthorizedInsurances disabled={disablePanels} />
          </InsurancesProvider>
        </div >
      ),
      "Competitive Insights": (
        <div className="col-12">
          <clientViews.ClientBenchmarking />
        </div >
      ),
      "Market Expansion": (
        <div className="col-12">
          <clientViews.ClientMarketExpansion />
        </div>
      ),
      "Snowflake Warehouse Settings": (
        <div className="col-8">
          <clientViews.ClientSnowflakeWarehouseSettings disabled={disablePanels || !(userIsSnowflakeWarehouseAdmin)} />
        </div >
      ),
      "Snowflake Data Sharing": (
        <div className="col-8">
          <clientViews.ClientDataSharing disabled={disablePanels || !userIsDataShareAdmin} />
        </div>
      ),
      "Provider Affiliation": (
        <div className="col-12">
          <clientViews.ClientAffiliationAuthorizedStates />
        </div >
      ),
    };

    if (!userIsQuestAdmin) {
      delete views["SSO"];
      delete views["Software Releases"];
    }

    if (!userIsDataShareAdmin && !userIsDataShareViewer) {
      delete views["Snowflake Data Sharing"];
    }

    return views;
  }, [disablePanels, clientContext.availableInsurances]);
  /* oxlint-enable sort-keys */

  const submitIsDisabled = (): boolean => {
    return loading || submitting ||
      !clientContext.formIsValid || clientContext.changedFields().length === 0 ||
      clientContext.authorizedInsurancesIsLoading || clientContext.availableInsurancesIsLoading ||
      clientContext.insurancesHaveErred;
  };

  const disableCreateGuestLogins = (): boolean => {
    const changedFields = clientContext.changedFields();
    // disable the "create guest logins" button if the "test logins allowed" or "guest logins allowed" checkboxes have been changed
    return loading || submitting ||
      (changedFields.length > 0 && (changedFields.includes("questViewerGuestLoginsAllowed") || changedFields.includes("resourceAdminGuestLoginsAllowed")));
  }

  const handleSubmit = () => {
    setSubmitting(true);

    const promises: Promise<void | SSOConfig>[] = [];
    promises.push(clientContext.updateClient());
    const SSOConfig = toSSOConfig(accountContext.ssoState.sso, clientContext.clientId);

    if (userIsQuestAdmin) {
      if (hasSSO) {
        promises.push(ssoSchemesService.patch(SSOConfig));
      } else if (clientContext.requiredFields["ssoDisplayName"]) {
        promises.push(ssoSchemesService.post(SSOConfig));
      }
    }

    Promise.all(promises).then(() => {
      toast(<span>{clientContext.clientName} has been updated.</span>,
        {
          containerId: "z-index-9999",
          onOpen: () => {
            // there's no way to know whether the browser CAN go back, so we'll set redirect as a fallback
            window.browserHistory.goBack();
            setRedirect(props.location.pathname);
          },
          type: "success",
        },
      );
    }, () => {
      toast(<span>We encountered a problem updating {clientContext.clientName}.</span>,
        {
          containerId: "z-index-9999",
          onClose: () => {
            setSubmitting(false);
          },
          type: "error",
        },
      );
    })
  }

  const handleCancel = (): void => {
    if (userIsQuestAdmin) {
      props.history.goBack();
      setRedirect(props.location.pathname);
    } else {
      setRedirect("/admin/dashboard")
    }
  }

  //const changedValues = React.useMemo(() => {
  //  return clientContext.changedFields().map((field) => <span key={field}>{field}, </span>);
  //}, [clientContext.changedFields]);

  const invalidFields = React.useMemo(() => {
    const keys = Object.entries(clientContext.validationObject).filter((entry) => !entry[1]).map((entry) => entry[0]);
    return keys.map((key) => <li key={key}>{clientContext.fieldNameToVisibleName[key]}</li>);
  }, [clientContext.validationObject]);

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  const handleExportClientProjectConfigs = async () => {
    setExportingProjectConfigs(true);
    await projectService.getProjectDetailsCsv(`Project Configurations(${clientContext.clientName})_${moment.utc().local().format("yyyyMMDD_hhmmss")}`, clientContext.clientId);
    setExportingProjectConfigs(false);
  }

  return (
    <Page documentTitle={`${clientContext.clientName || "Loading"} - Client Details - Admin`}>
      <Breadcrumbs template={BreadcrumbTemplateEnum.ClientAdministration} items={breadcrumbs} />
      <PageInner>
        <PageHeader title={`${clientContext.clientName || ""}${clientContext.isActive === false ? " (Inactive)" : ""}`} fa-icon="fa-briefcase" disableHR={false}>
          <PageHeaderRight>
            {clientContext.clientId !== undefined && (
              clientContext.isActive ? (
                <>
                  <RoleRestricted allowedRoles={[Roles.QUEST_ADMIN]}>
                    <ModalLink
                      content={{
                        body: "Are you sure you want to deactivate this client?",
                        cancelButtonText: "Cancel",
                        confirmButtonAction: deactivateClient,
                        confirmButtonText: "Confirm",
                        confirmButtonType: "primary-danger",
                        title: "Deactivate Client",
                      }}
                      classNames="me-3 qes-button-secondary danger"
                      testId="client-detail__deactivate-client-modal"
                      disabled={clientContext.clientId === undefined}
                      hideBodyScroll={true}
                    >
                      Deactivate Client
                    </ModalLink>
                  </RoleRestricted>
                  <Link
                    className="qes-button qes-button-secondary"
                    data-testid="client-details__add-users"
                    to={{
                      pathname: "/admin/user-administration/create",
                      state: {
                        clientId: clientContext.clientId,
                      },
                    }}
                  >
                    Add a New User
                  </Link>
                  <RoleRestricted allowedRoles={[Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN]}>
                    <Link
                      className="qes-button qes-button-secondary"
                      data-testid="client-details__add-bulk-users"
                      to={{
                        pathname: "/admin/user-administration/bulk-create",
                        state: {
                          clientId: clientContext.clientId,
                        },
                      }}
                    >
                      Add New Users in Bulk
                    </Link>
                    {(userIsQuestAdmin || user.clientId === clientContext.clientId) ?
                      <Link
                        className="qes-button qes-button-secondary"
                        data-testid="client-details__role-groups"
                        to={{
                          pathname: `/admin/client-administration/${clientContext.clientId}/role-groups`,
                          state: {
                            clientName: clientContext.clientName
                          }
                        }}
                      >
                        Role Groups
                      </Link> : null
                    }
                    <Link
                      className="qes-button qes-button-secondary"
                      data-testid="client-details__role-assignments"
                      to={{
                        pathname: `/admin/internal-reports/role-assignments/${(clientContext.clientId !== 1 ? clientContext.clientId : "")}`,
                      }}
                    >
                      Role Assignments
                    </Link>
                  </RoleRestricted>
                  <Link
                    className="qes-button qes-button-secondary"
                    data-testid="client-details__login-statistics"
                    to={{
                      pathname: `/admin/login-statistics/${clientContext.clientId}`
                    }}
                  >
                    Login Statistics
                  </Link>
                  <ModalLink
                    content={{
                      body: "Are you sure you want to create guest logins for this client?",
                      cancelButtonText: "Cancel",
                      confirmButtonAction: createGuestLogins,
                      confirmButtonText: "Confirm",
                      title: "Create Guest Logins",
                    }}
                    classNames="qes-button-secondary"
                    testId="client-detail__create-guest-logins-modal"
                    disabled={clientContext.clientId === undefined || disableCreateGuestLogins()}
                    hideBodyScroll={true}
                  >
                    Create Guest Logins
                  </ModalLink>
                </>
              ) : (
                <>
                  <RoleRestricted allowedRoles={[Roles.QUEST_ADMIN]}>
                    <ModalLink
                      content={{
                        body: "Are you sure you want to activate this client?",
                        cancelButtonText: "Cancel",
                        confirmButtonAction: activateClient,
                        confirmButtonText: "Confirm",
                        title: "Activate Client",
                      }}
                      classNames="me-3 qes-button-secondary"
                      testId="client-detail__activate-client-modal"
                      disabled={clientContext.clientId === undefined}
                      hideBodyScroll={true}
                    >
                      Activate Client
                    </ModalLink>
                  </RoleRestricted>
                </>
              )
            )}
            {clientContext.clientId !== undefined ? (
              <RoleRestricted allowedRoles={[Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN]}>
                <QESButton
                  qesTestId="export-project-configs-all"
                  qesType="secondary"
                  onClick={() => handleExportClientProjectConfigs()}
                  disabled={exportingProjectConfigs}
                  className="clients-admin-export-btn"
                >
                  Export Client Project Configuration
                </QESButton>
              </RoleRestricted>
            ) : null}
          </PageHeaderRight>
        </PageHeader>
        <PageSection>
          {invalidFields.length > 0 && clientContext.isActive && !loading ? (
            <QESAlert type="danger" collapseTitle="Invalid Fields">
              {invalidFields}
            </QESAlert>
          ) : null}
          <div className="tabs-style-2">
            <ul className="nav nav-tabs">
              {Object.keys(clientDetailViews).map((key) => (
                <li className="nav-item" key={key}>
                  <a className={`nav-link${activeView === key ? " active" : ""}`} role="tab" onClick={() => setActiveView(key as ClientViews)}>{key}</a>
                </li>
              ))}
            </ul>
          </div>
          {!!error && <QESAlert type="danger" persist={true}>{error}</QESAlert>}
          {loading && <div className="mt-3"><LoadingSpinner /></div>}
          {!loading && (
            <div className="row mt-3">
              {clientDetailViews[activeView]}
            </div>
          )}
        </PageSection>
      </PageInner>
      <WizardNavigationBar
        leftSide={<QESButton qesType="secondary-danger" qesTestId="create-user__cancel" onClick={handleCancel}>Cancel</QESButton>}
        rightSide={
          <QESButton
            qesType="primary"
            onClick={handleSubmit}
            qesTestId="new-client__create"
            disabled={submitIsDisabled()}
          >
            Update
          </QESButton>
        }
      />
    </Page>
  );
}

export const ClientDetail: React.FC<React.PropsWithChildren<PropsType>> = (props: PropsType) => {
  return (
    <AccountProvider>
      <ClientProvider clientId={parseInt(props.match.params.clientId, 10)}>
        <DashboardsProvider>
          <ClientDetailWithContexts {...props} />
        </DashboardsProvider>
      </ClientProvider>
    </AccountProvider>
  );
}
